import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Button } from './ui/button';

/**
 * Searchable single-select. `options`: [{ value, label, hint? }]
 * `value` = selected value string ('' for none). `onChange(value)`.
 */
export function Combobox({ options = [], value = '', onChange, placeholder = 'Select…', clearable = true, testId = 'combobox', className = '', disabled = false }) {
    const [open, setOpen] = useState(false);
    const selected = options.find(o => o.value === value);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    disabled={disabled}
                    aria-expanded={open}
                    className={`w-full justify-between font-normal ${className}`}
                    data-testid={`${testId}-trigger`}
                >
                    <span className={`truncate ${selected ? '' : 'text-neutral-500'}`}>
                        {selected ? selected.label : placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                    <CommandInput placeholder="Search…" data-testid={`${testId}-search`} />
                    <CommandList>
                        <CommandEmpty>No match found.</CommandEmpty>
                        <CommandGroup>
                            {clearable && (
                                <CommandItem value=" " onSelect={() => { onChange(''); setOpen(false); }} data-testid={`${testId}-item-clear`}>
                                    <X className="mr-2 h-3.5 w-3.5 text-neutral-400" />
                                    <span className="text-neutral-500 italic">Clear selection</span>
                                </CommandItem>
                            )}
                            {options.map(o => (
                                <CommandItem
                                    key={o.value}
                                    value={`${o.label} ${o.hint || ''}`}
                                    onSelect={() => { onChange(o.value); setOpen(false); }}
                                    data-testid={`${testId}-item-${o.value}`}
                                >
                                    <Check className={`mr-2 h-4 w-4 ${value === o.value ? 'opacity-100 text-green-600' : 'opacity-0'}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="truncate text-sm">{o.label}</div>
                                        {o.hint && <div className="truncate text-xs text-neutral-500">{o.hint}</div>}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

/**
 * Searchable multi-select with pill chips.
 */
export function MultiCombobox({ options = [], value = [], onChange, placeholder = 'Select…', testId = 'multicombo' }) {
    const [open, setOpen] = useState(false);
    const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
    const selected = options.filter(o => value.includes(o.value));
    return (
        <div>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="w-full justify-between font-normal" data-testid={`${testId}-trigger`}>
                        <span className="truncate text-neutral-500">
                            {selected.length ? `${selected.length} selected` : placeholder}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                        <CommandInput placeholder="Search…" data-testid={`${testId}-search`} />
                        <CommandList>
                            <CommandEmpty>No match found.</CommandEmpty>
                            <CommandGroup>
                                {options.map(o => {
                                    const on = value.includes(o.value);
                                    return (
                                        <CommandItem key={o.value} value={`${o.label} ${o.hint || ''}`} onSelect={() => toggle(o.value)} data-testid={`${testId}-item-${o.value}`}>
                                            <Check className={`mr-2 h-4 w-4 ${on ? 'opacity-100 text-green-600' : 'opacity-0'}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="truncate text-sm">{o.label}</div>
                                                {o.hint && <div className="truncate text-xs text-neutral-500">{o.hint}</div>}
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {selected.map(o => (
                        <span key={o.value} className="inline-flex items-center gap-1 rounded bg-green-100 text-green-800 px-2 py-0.5 text-xs">
                            {o.label}
                            <button type="button" onClick={() => toggle(o.value)} data-testid={`${testId}-remove-${o.value}`}><X className="h-3 w-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Combobox;
